import React from 'react'
import { Maybe, MediaItem, MediaItemSizeEnum } from '.codegen/generatedApi'
import { Box, BoxProps, Fade, Stack, StackProps } from '@mui/material'
import { asNumber, isNullish, parseImgMedia } from 'helpers'
import { ImgMedia } from 'types'
import { selectPacificSticker, selectPngSticker } from 'app/selectors'
import { useAppSelector } from 'app/hooks'

export type ImageProps = BoxProps<
  'img',
  {
    object?: Maybe<MediaItem>
    imageData?: ImgMedia | null
    stickerType?: string | null
    stickerImage?: Maybe<MediaItem>
    // crop?: [height: number | undefined, width: number | undefined ],
    url?: string
    fillPercentage?: number
    fillPercentageSticker?: number
    size?: `${MediaItemSizeEnum}`
    contain?: boolean
    responsive?: boolean
    ratio?: number
    imgProps?: BoxProps<'img'>
    childWrapperProps?: StackProps
    withBorder?: boolean
  }
>

const Image = ({
  imageData,
  stickerType,
  stickerImage,
  fillPercentage = 100,
  object,
  // crop,
  contain,
  ratio = 1,
  responsive = true,
  size = MediaItemSizeEnum.Thumbnail,
  sx = {},
  url,
  withBorder,
  imgProps: { sx: imgSx = {}, ...imgProps } = {},
  childWrapperProps: { sx: childWrapperSx, ...childWrapperProps } = {},
  children,
  ...props
}: ImageProps) => {
  const img = imageData ? imageData : parseImgMedia(object)

  const { altText, sizes, width: w, height: h, url: src } = img

  const maybeResized = null //{result: maybeResized } = useResizedImage(src, { maxHeight: crop ? crop[0] : -1, maxWidth: crop ? crop[1] : -1  })

  let srcObject = sizes.find(
    (v) => v.name && v.name.toUpperCase() === size
  ) || {
    sourceUrl: maybeResized ? maybeResized : src,
    width: maybeResized ? null : w,
    height: maybeResized ? null : h,
  }

  const { sourceUrl = '' } = srcObject

  const imgRef = React.useRef<HTMLImageElement>()
  const [loaded, setLoaded] = React.useState(false)
  const [[height, width], setDimensions] = React.useState([
    asNumber(srcObject.height),
    asNumber(srcObject.width),
  ])

  const listener = React.useCallback(
    function () {
      setLoaded(true)
      if (!imgRef.current) return
      setDimensions([imgRef.current.naturalHeight, imgRef.current.naturalWidth])
    },
    [setLoaded, setDimensions, imgRef]
  )

  React.useEffect(() => {
    if (!imgRef.current) return
    const currentImg = imgRef.current
    currentImg.addEventListener('load', listener)
    return () => {
      currentImg.removeEventListener('load', listener)
    }
  }, [imgRef, setDimensions, setLoaded, listener])

  let stickerBox;
  let stickerUrl;

  const pacificSticker = useAppSelector(selectPacificSticker)

  const pngSticker = useAppSelector(selectPngSticker)

  if (stickerType === 'pacific') {

    stickerUrl = pacificSticker.sourceUrl;

  } else if (stickerType === 'png') {

    stickerUrl = pngSticker?.sourceUrl;

  } else if (stickerType === 'custom') {

    stickerUrl = stickerImage?.sourceUrl;
  }

  if (stickerUrl) {
    stickerBox = <Box
                  component="img"
                  ref={imgRef}
                  {...{
                    alt: altText,
                    src: stickerUrl,
                    width: !isNullish(width) ? width : w,
                    height: !isNullish(height) ? height : h,
                    ...imgProps,
                  }}
                  sx={{
                    ...(ratio && {
                      position: 'absolute',
                      top: '18%',
                      left: '82%',
                      transform: 'translate(-50%, -50%)',
                      maxWidth: `31%`,
                      maxHeight: `31%`,
                    }),
                    ...(!ratio &&
                      responsive && {
                        maxWidth: '100%',
                        height: 'auto',
                      }),
                    ...imgSx,
                  }}
                />;
  }

  return (
    <Box
      {...props}
      sx={{
        ...(ratio && {
          width: '100%',
          paddingTop: `${ratio * 100}%`,
          position: 'relative',
          bgcolor: 'common.white',
          overflow: 'hidden',
        }),
        ...(withBorder && {
          border: '1px inset',
          borderColor: 'divider',
        }),
        ...sx,
      }}
    >
      <Fade in={loaded}>
        <Box>
          <Box
            component="img"
            ref={imgRef}
            {...{
              alt: altText,
              src: !isNullish(sourceUrl) ? sourceUrl : '',
              width: !isNullish(width) ? width : w,
              height: !isNullish(height) ? height : h,
              ...imgProps,
            }}
            sx={{
              ...(ratio && {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                ...(contain
                  ? {
                      maxWidth: `${fillPercentage}%`,
                      maxHeight: `${fillPercentage}%`,
                      height: 'auto',
                      width: 'auto',
                    }
                  : width <= height
                  ? {
                      maxHeight: 'none',
                      maxWidth: `${fillPercentage}%`,
                      height: 'auto',
                      width: `${fillPercentage}%`,
                    }
                  : {
                      maxHeight: `${fillPercentage}%`,
                      maxWidth: 'none',
                      height: `${fillPercentage}%`,
                      width: 'auto',
                    }),
              }),
              ...(!ratio &&
                responsive && {
                  maxWidth: '100%',
                  height: 'auto',
                }),
              ...imgSx,
            }}
          />
          {stickerBox}
          {children && (
            <Stack
              {...childWrapperProps}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                ...childWrapperSx,
              }}
            >
              {children}
            </Stack>
          )}
        </Box>
      </Fade>
    </Box>
  )
}

export default Image
